import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL;

/** A host the current user follows, as shown in the dashboard list. */
export interface FollowedHost {
  hostId: string;
  displayName: string;
  avatarUrl: string | null;
  followedAt: string;
}

/** What the token-authenticated unfollow page renders. */
export interface UnfollowContext {
  hostDisplayName: string;
  following: boolean;
  /** True once the user has stopped every promotional email, so the page hides that option. */
  optedOutOfAll: boolean;
}

/** Thrown so the page can tell an expired link apart from a broken one. */
export class HostFollowApiError extends Error {
  constructor(
    public readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'HostFollowApiError';
  }
}

async function authHeaders(): Promise<HeadersInit> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('Not authenticated');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function unwrap<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.success) {
    throw new HostFollowApiError(
      body?.error?.code || 'REQUEST_FAILED',
      body?.error?.message || `Request failed (${res.status})`
    );
  }
  return body.data as T;
}

export const hostFollowApi = {
  async getStatus(hostId: string): Promise<boolean> {
    const res = await fetch(`${API_URL}/hosts/${hostId}/follow`, {
      headers: await authHeaders(),
    });
    const data = await unwrap<{ following: boolean }>(res);
    return data.following;
  },

  async follow(hostId: string): Promise<void> {
    const res = await fetch(`${API_URL}/hosts/${hostId}/follow`, {
      method: 'POST',
      headers: await authHeaders(),
    });
    await unwrap(res);
  },

  async unfollow(hostId: string): Promise<void> {
    const res = await fetch(`${API_URL}/hosts/${hostId}/follow`, {
      method: 'DELETE',
      headers: await authHeaders(),
    });
    await unwrap(res);
  },

  async listMine(): Promise<FollowedHost[]> {
    const res = await fetch(`${API_URL}/hosts/follows/me`, {
      headers: await authHeaders(),
    });
    return unwrap<FollowedHost[]>(res);
  },

  // --- Token-authenticated: no login, called from the email link ---

  async getUnfollowContext(token: string): Promise<UnfollowContext> {
    const res = await fetch(
      `${API_URL}/public/host-follows/context?token=${encodeURIComponent(token)}`
    );
    return unwrap<UnfollowContext>(res);
  },

  async unfollowByToken(token: string): Promise<void> {
    const res = await fetch(`${API_URL}/public/host-follows/unfollow`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    await unwrap(res);
  },

  /**
   * Stop every promotional email from YOYO Club, newsletter included. The
   * second, explicitly labelled choice on the unfollow page: reachable
   * without a login, because that is what a legal opt-out has to be.
   */
  async optOutOfAllByToken(token: string): Promise<void> {
    const res = await fetch(`${API_URL}/public/host-follows/opt-out-all`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    await unwrap(res);
  },

  // Nothing else by token: the link authorizes opting out, never opting back
  // in. Following again, or managing every follow, goes through the
  // authenticated calls above.
};
